import { useState, useRef, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import { useToast } from '../contexts/ToastContext';
import Modal from './Modal';
import { Car, Save, Hash, Calendar, Loader2, FileText, Upload, CheckCircle, ExternalLink } from 'lucide-react';
import {
  VEHICLE_MANUFACTURERS,
  VEHICLE_COLORS,
  getVehicleYearOptions,
  getModelsForMake,
  TAXI_OPERATING_LICENCE_INFO_URL,
} from '../data/vehicleFormOptions';

const toVehicleType = (name = '') => {
  const n = name.toLowerCase();
  if (n === 'premium') return 'premium';
  if (n === 'luxury') return 'luxury';
  return 'standard';
};

const VEHICLE_DOC_TYPES = {
  taximeter: { type: 'taximeter', label: 'Taximeter' },
  roofLight: { type: 'roof_light', label: 'Roof light' },
  vehicleInsurance: { type: 'vehicle_insurance', label: 'Vehicle insurance documentation' },
  taxiOperatingLicense: { type: 'taxi_operating_license', label: 'Taxi operating license' },
};

const initialDocs = {
  taximeter: null,
  roofLight: null,
  vehicleInsurance: null,
  taxiOperatingLicense: null,
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function mapRowToDoc(row, fallbackLabel, typeKey) {
  return {
    fileName: row.file_name || 'Uploaded',
    mimeType: row.mime_type || 'application/octet-stream',
    type: typeKey,
    displayName: row.display_name || fallbackLabel,
    existing: true,
    url: row.url,
  };
}

function FileUploadField({ docKey, document, onUpload, label, description, required, isEditMode, uploadLabel }) {
  const inputRef = useRef(null);
  const btnText = uploadLabel || 'Upload file';
  return (
    <div>
      <label className="block text-sm font-semibold text-text dark:text-text-dark mb-1">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {description ? (
        <p className="text-sm text-text-secondary dark:text-text-secondary-dark mb-3">{description}</p>
      ) : null}
      {isEditMode && (
        <div className="mb-2 rounded-lg border border-border dark:border-border-dark bg-surface-secondary/80 px-3 py-2.5">
          {document?.existing ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-start gap-2 min-w-0">
                <FileText className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{document.fileName}</p>
                  <p className="text-xs text-text-tertiary dark:text-text-tertiary-dark">Previously uploaded</p>
                </div>
              </div>
              {document.url ? (
                <a
                  href={document.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary inline-flex items-center gap-1 shrink-0"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> View
                </a>
              ) : null}
            </div>
          ) : document?.base64Data ? (
            <p className="text-sm text-success">New file: {document.fileName}</p>
          ) : (
            <p className="text-sm text-text-secondary dark:text-text-secondary-dark">No file on record — upload below to add.</p>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(docKey, file);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border-2 border-dashed transition-colors ${
          document ? 'border-success bg-success/5' : 'border-border dark:border-border-dark bg-surface-secondary dark:bg-surface-secondary-dark hover:border-primary'
        }`}
      >
        {document ? <CheckCircle className="w-5 h-5 text-success shrink-0" /> : <Upload className="w-5 h-5 shrink-0" />}
        <span className={`text-sm ${document ? 'text-success font-medium' : 'text-text-secondary'}`}>
          {document ? (document.existing ? `${document.fileName} (on file)` : document.fileName) : btnText}
        </span>
      </button>
    </div>
  );
}

function mapApiDocs(rows) {
  const next = { ...initialDocs };
  if (!Array.isArray(rows)) return next;
  const byType = {};
  rows.forEach((d) => {
    const t = d.document_type || d.type;
    if (t) byType[t] = d;
  });
  Object.entries(VEHICLE_DOC_TYPES).forEach(([key, { type }]) => {
    const row = byType[type];
    if (row) {
      next[key] = mapRowToDoc(row, VEHICLE_DOC_TYPES[key].label, type);
    }
  });

  // Legacy uploads from previous registration / insurance flow
  if (!next.taximeter && byType.vehicle_registration) {
    next.taximeter = mapRowToDoc(byType.vehicle_registration, 'Vehicle registration (legacy)', 'vehicle_registration');
  }
  if (!next.vehicleInsurance && byType.insurance) {
    next.vehicleInsurance = mapRowToDoc(byType.insurance, 'Insurance proof (legacy)', 'insurance');
  }

  return next;
}

const selectClass = (hasError) =>
  `w-full px-4 py-3 rounded-xl border bg-surface-secondary dark:bg-surface-secondary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${
    hasError ? 'border-danger' : 'border-border dark:border-border-dark'
  }`;

const inputClass = (hasError) =>
  `w-full px-4 py-3 rounded-xl border bg-surface-secondary dark:bg-surface-secondary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${
    hasError ? 'border-danger' : 'border-border dark:border-border-dark'
  }`;

export default function AddVehicleModal({ isOpen, onClose, mode = 'create', vehicleId = null }) {
  const isEdit = mode === 'edit' && vehicleId;
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear() + 1,
    color: '',
    licensePlate: '',
    transportLicenceNumber: '',
    vehicleComment: '',
    vehicleCategoryId: '',
    capacity: 4,
  });
  const [documents, setDocuments] = useState(initialDocs);
  const [errors, setErrors] = useState({});
  const [hydrated, setHydrated] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const yearOptions = getVehicleYearOptions();
  const modelOptions = useMemo(() => {
    const base = getModelsForMake(formData.make);
    if (formData.model && !base.includes(formData.model)) {
      return [formData.model, ...base];
    }
    return base;
  }, [formData.make, formData.model]);

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['fleet-vehicle-categories'],
    queryFn: () => fleetService.getVehicleCategories().then((r) => r.data),
    enabled: isOpen,
  });
  const categories = categoriesData?.categories || [];

  const { data: vehicleRes, isLoading: vehicleLoading } = useQuery({
    queryKey: ['fleet-vehicle-edit', vehicleId],
    queryFn: () => fleetService.getVehicle(vehicleId).then((r) => r.data?.data ?? r.data),
    enabled: Boolean(isOpen && isEdit && vehicleId),
  });

  useEffect(() => {
    if (!isOpen) {
      setHydrated(false);
      setStep(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isEdit || !vehicleRes || hydrated) return;
    const v = vehicleRes;
    setFormData({
      make: v.make || '',
      model: v.model && v.model !== '—' ? v.model : '',
      year: v.year || new Date().getFullYear(),
      color: v.color || '',
      licensePlate: v.license_plate || v.licensePlate || '',
      transportLicenceNumber: v.transport_licence_number || v.transportLicenceNumber || '',
      vehicleComment: v.vehicle_comment || v.vehicleComment || '',
      vehicleCategoryId: v.vehicle_category_id || '',
      capacity: v.capacity || 4,
    });
    setDocuments(mapApiDocs(v.documents));
    setHydrated(true);
  }, [isOpen, isEdit, vehicleRes, hydrated]);

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      isEdit ? fleetService.updateVehicle(vehicleId, payload) : fleetService.createVehicle(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['fleet-vehicles']);
      if (vehicleId) {
        queryClient.invalidateQueries(['fleet-vehicle', vehicleId]);
        queryClient.invalidateQueries(['fleet-vehicle-edit', vehicleId]);
      }
      const msg = res.data?.message || (isEdit ? 'Vehicle updated.' : 'Vehicle submitted for approval.');
      showSuccess(msg);
      handleClose();
    },
    onError: (error) => {
      showError(error.response?.data?.message || error.response?.data?.error || 'Request failed');
      if (error.response?.data?.errors) setErrors(error.response.data.errors);
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'make') {
      setFormData((prev) => ({
        ...prev,
        make: value,
        model: '',
      }));
    } else if (name === 'vehicleCategoryId') {
      const cat = categories.find((c) => c.id === value);
      setFormData((prev) => ({
        ...prev,
        vehicleCategoryId: value,
        capacity: cat?.capacity ?? prev.capacity,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === 'year' || name === 'capacity' ? parseInt(value, 10) || value : value,
      }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleDocUpload = async (docKey, file) => {
    try {
      const base64Data = await fileToBase64(file);
      setDocuments((prev) => ({
        ...prev,
        [docKey]: {
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
          base64Data,
          type: VEHICLE_DOC_TYPES[docKey].type,
          displayName: VEHICLE_DOC_TYPES[docKey].label,
        },
      }));
    } catch {
      showError('Failed to read file');
    }
  };

  const hasDoc = (k) => {
    const d = documents[k];
    return d && (d.base64Data || d.existing);
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.make) newErrors.make = 'Vehicle manufacturer is required';
    if (!formData.model) newErrors.model = 'Vehicle model is required';
    if (!formData.year) newErrors.year = 'Vehicle year is required';
    if (!formData.color) newErrors.color = 'Vehicle color is required';
    if (!formData.licensePlate?.trim()) newErrors.licensePlate = 'Licence plate is required';
    if (!formData.transportLicenceNumber?.trim()) newErrors.transportLicenceNumber = 'Transport licence number is required';
    return newErrors;
  };

  const handleContinue = () => {
    setErrors({});
    const newErrors = validateStep1();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setStep(2);
  };

  const handleFinalSubmit = () => {
    setErrors({});
    const newErrors = { ...validateStep1() };
    if (!formData.vehicleCategoryId && categories.length > 0) newErrors.vehicleCategoryId = 'Select a category';
    if (!hasDoc('taximeter')) newErrors.taximeterDoc = 'Taximeter document is required';
    if (!hasDoc('roofLight')) newErrors.roofLightDoc = 'Roof light document is required';
    if (!hasDoc('taxiOperatingLicense')) newErrors.taxiOperatingLicenseDoc = 'Taxi operating license is required';

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      if (Object.keys(validateStep1()).some((k) => newErrors[k])) setStep(1);
      return;
    }

    const selectedCategory = categories.find((c) => c.id === formData.vehicleCategoryId);
    const docPayload = Object.values(documents)
      .filter((d) => d && d.base64Data)
      .map((d) => ({
        type: d.type,
        displayName: d.displayName,
        fileName: d.fileName,
        mimeType: d.mimeType,
        fileSize: d.size,
        base64Data: d.base64Data,
      }));

    if (!isEdit) {
      const requiredTypes = ['taximeter', 'roof_light', 'taxi_operating_license'];
      const uploadedTypes = docPayload.map((d) => d.type);
      const hasAllNew = requiredTypes.every((t) => uploadedTypes.includes(t));
      if (!hasAllNew) {
        setErrors({ _form: 'Please upload taximeter, roof light, and taxi operating license.' });
        return;
      }
    }

    const payload = {
      make: formData.make,
      model: formData.model,
      year: parseInt(formData.year, 10),
      color: formData.color,
      licensePlate: formData.licensePlate.trim(),
      transportLicenceNumber: formData.transportLicenceNumber.trim(),
      vehicleComment: formData.vehicleComment?.trim() || null,
      vehicleType: toVehicleType(selectedCategory?.name),
      vehicleCategoryId: formData.vehicleCategoryId || null,
      capacity: selectedCategory?.capacity ?? parseInt(formData.capacity, 10),
      insuranceDetails: null,
      documents: docPayload,
    };

    saveMutation.mutate(payload);
  };

  const handleClose = () => {
    setStep(1);
    setFormData({
      make: '',
      model: '',
      year: new Date().getFullYear() + 1,
      color: '',
      licensePlate: '',
      transportLicenceNumber: '',
      vehicleComment: '',
      vehicleCategoryId: '',
      capacity: 4,
    });
    setDocuments(initialDocs);
    setErrors({});
    setHydrated(false);
    onClose();
  };

  if (isOpen && isEdit && vehicleLoading && !vehicleRes) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Edit vehicle" size="lg">
        <div className="flex justify-center py-16">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
        </div>
      </Modal>
    );
  }

  const modalTitle = isEdit ? 'Edit vehicle' : 'Add vehicle';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={modalTitle} size="lg">
      <div className="space-y-6">
        {errors._form && <p className="text-sm text-danger">{errors._form}</p>}

        <div className="bg-info-light dark:bg-info-dark/20 border border-info/30 rounded-xl p-4">
          <p className="text-sm text-info dark:text-info-dark">
            New and updated vehicles are sent to <strong>admin for approval</strong>. Only approved vehicles can be assigned to drivers or used on trips.
          </p>
        </div>

        <div className="flex gap-2 h-2 rounded-full overflow-hidden bg-surface-secondary dark:bg-surface-secondary-dark">
          <div
            className={`flex-1 rounded-full transition-colors ${step >= 1 ? 'bg-primary' : 'bg-border dark:bg-border-dark'}`}
          />
          <div
            className={`flex-1 rounded-full transition-colors ${
              step >= 2 ? 'bg-primary/45 dark:bg-primary/50' : 'bg-border dark:bg-border-dark'
            }`}
          />
        </div>

        {step === 1 && (
          <div>
            <h3 className="text-lg font-semibold text-text dark:text-text-dark mb-4 flex items-center gap-2">
              <Car className="w-5 h-5 text-primary" />
              Vehicle information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Vehicle manufacturer <span className="text-danger">*</span>
                </label>
                <select name="make" value={formData.make} onChange={handleChange} className={selectClass(!!errors.make)}>
                  <option value="">Vehicle manufacturer</option>
                  {VEHICLE_MANUFACTURERS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                {errors.make && <p className="text-xs text-danger mt-1">{errors.make}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Vehicle model <span className="text-danger">*</span>
                </label>
                <select
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  disabled={!formData.make}
                  className={selectClass(!!errors.model)}
                >
                  <option value="">{formData.make ? 'Vehicle model' : 'Select manufacturer first'}</option>
                  {modelOptions.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                {errors.model && <p className="text-xs text-danger mt-1">{errors.model}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Vehicle year <span className="text-danger">*</span>
                </label>
                <select name="year" value={formData.year} onChange={handleChange} className={selectClass(!!errors.year)}>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                {errors.year && <p className="text-xs text-danger mt-1">{errors.year}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Hash className="w-4 h-4" /> Licence plate <span className="text-danger">*</span>
                </label>
                <input
                  name="licensePlate"
                  value={formData.licensePlate}
                  onChange={handleChange}
                  className={`${inputClass(!!errors.licensePlate)} font-mono`}
                  placeholder="Licence plate"
                />
                {errors.licensePlate && <p className="text-xs text-danger mt-1">{errors.licensePlate}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Vehicle color <span className="text-danger">*</span>
                </label>
                <select name="color" value={formData.color} onChange={handleChange} className={selectClass(!!errors.color)}>
                  <option value="">Vehicle color</option>
                  {VEHICLE_COLORS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {errors.color && <p className="text-xs text-danger mt-1">{errors.color}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Transport licence number <span className="text-danger">*</span>
                </label>
                <input
                  name="transportLicenceNumber"
                  value={formData.transportLicenceNumber}
                  onChange={handleChange}
                  className={inputClass(!!errors.transportLicenceNumber)}
                  placeholder="Transport licence number"
                />
                {errors.transportLicenceNumber && (
                  <p className="text-xs text-danger mt-1">{errors.transportLicenceNumber}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Comment</label>
                <input
                  name="vehicleComment"
                  value={formData.vehicleComment}
                  onChange={handleChange}
                  className={inputClass(false)}
                  placeholder="Comment"
                />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-text dark:text-text-dark mb-2">Vehicle documents</h3>
              <p className="text-sm text-text-secondary dark:text-text-secondary-dark mb-6">
                These documents are legally required. Scans or high-quality photos are acceptable. We cannot accept photos of photos.
              </p>
              <div className="space-y-8">
                <FileUploadField
                  docKey="taximeter"
                  document={documents.taximeter}
                  onUpload={handleDocUpload}
                  label={VEHICLE_DOC_TYPES.taximeter.label}
                  description="Add a picture of your taximeter."
                  required
                  isEditMode={isEdit}
                  uploadLabel="Upload file"
                />
                {errors.taximeterDoc && <p className="text-xs text-danger -mt-4">{errors.taximeterDoc}</p>}

                <FileUploadField
                  docKey="roofLight"
                  document={documents.roofLight}
                  onUpload={handleDocUpload}
                  label={VEHICLE_DOC_TYPES.roofLight.label}
                  description="Please provide a picture of a roof light as this is required in Norway after 1/1/2023."
                  required
                  isEditMode={isEdit}
                  uploadLabel="Upload file"
                />
                {errors.roofLightDoc && <p className="text-xs text-danger -mt-4">{errors.roofLightDoc}</p>}

                <FileUploadField
                  docKey="vehicleInsurance"
                  document={documents.vehicleInsurance}
                  onUpload={handleDocUpload}
                  label={VEHICLE_DOC_TYPES.vehicleInsurance.label}
                  description="Please provide a copy of your vehicle insurance."
                  required={false}
                  isEditMode={isEdit}
                  uploadLabel="Upload file"
                />

                <div>
                  <FileUploadField
                    docKey="taxiOperatingLicense"
                    document={documents.taxiOperatingLicense}
                    onUpload={handleDocUpload}
                    label={VEHICLE_DOC_TYPES.taxiOperatingLicense.label}
                    description={
                      <>
                        In order to drive taxi in Norway you (or the fleet owner you are working with) must have a taxi operating
                        license. Please upload your taxi operating license in this section so that we can approve your application.
                        Read more about taxi operating licence{' '}
                        <a
                          href={TAXI_OPERATING_LICENCE_INFO_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary font-medium underline underline-offset-2"
                        >
                          here
                        </a>
                        .
                      </>
                    }
                    required
                    isEditMode={isEdit}
                    uploadLabel="Upload file"
                  />
                  {errors.taxiOperatingLicenseDoc && (
                    <p className="text-xs text-danger mt-1">{errors.taxiOperatingLicenseDoc}</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text dark:text-text-dark mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-primary" />
                Category & capacity
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Vehicle category *</label>
                  {categoriesLoading ? (
                    <div className="flex items-center gap-2 px-4 py-3 border rounded-xl">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                  ) : categories.length === 0 ? (
                    <div className="px-4 py-3 border border-warning/30 rounded-xl text-sm text-warning">No categories configured.</div>
                  ) : (
                    <select
                      name="vehicleCategoryId"
                      value={formData.vehicleCategoryId}
                      onChange={handleChange}
                      className={selectClass(!!errors.vehicleCategoryId)}
                    >
                      <option value="">— Select —</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.display_name}
                          {cat.capacity ? ` (${cat.capacity} seats)` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.vehicleCategoryId && <p className="text-xs text-danger mt-1">{errors.vehicleCategoryId}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Capacity (seats) *</label>
                  <input
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    min={2}
                    max={8}
                    className="w-full px-4 py-3 rounded-xl border border-border dark:border-border-dark bg-surface-secondary dark:bg-surface-secondary-dark focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4 border-t border-border dark:border-border-dark">
          <button
            type="button"
            onClick={handleClose}
            className="px-6 py-3 border border-border dark:border-border-dark rounded-full font-medium"
          >
            Cancel
          </button>
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end flex-1">
            {step === 2 && (
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setErrors({});
                }}
                className="px-6 py-3 border border-border dark:border-border-dark rounded-full font-medium"
              >
                Back
              </button>
            )}
            {step === 1 ? (
              <button
                type="button"
                onClick={handleContinue}
                className="w-full sm:w-auto min-w-[200px] px-8 py-3.5 bg-primary text-white rounded-full font-semibold shadow-lg hover:opacity-95 transition-opacity"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={handleFinalSubmit}
                disabled={saveMutation.isPending}
                className="w-full sm:w-auto min-w-[200px] flex items-center justify-center gap-2 px-8 py-3.5 bg-primary text-white rounded-full disabled:opacity-50 font-semibold shadow-lg"
              >
                <Save className="w-4 h-4" />
                {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
